import { useLayoutEffect } from "react";
import { create } from "zustand";

/**
 * Comments beside the chat (DESIGN.md "Comments sit beside the chat").
 *
 * Only a page that registers comments turns the rail's head into two tabs,
 * Comments and Chat with M7; today that is the team's calendar post page, and
 * every other page keeps the chat rail as it was. The page owns the comments
 * (their data, the composer, what failed to send) and portals them into the
 * slot the rail publishes here, so the rail never learns what a comment is.
 */
export type RailCommentsInfo = {
  /** Open threads, for the tab and the collapsed DeskTab. */
  count: number;
  /** One sentence of real state under the tabs, or null. */
  subtitle: string | null;
};

type RailCommentsStore = {
  info: RailCommentsInfo | null;
  /** Where the page portals the Comments tab's content. */
  slot: HTMLElement | null;
  setInfo: (info: RailCommentsInfo | null) => void;
  setSlot: (slot: HTMLElement | null) => void;
};

export const useRailCommentsStore = create<RailCommentsStore>()((set) => ({
  info: null,
  slot: null,
  setInfo: (info) => set({ info }),
  setSlot: (slot) => set({ slot }),
}));

/**
 * Register this page's comments with the rail for as long as it is mounted.
 * Returns the rail's slot to portal into (null while the rail has none, as in
 * the sheet layouts, where the page shows its own sheet instead).
 */
export function useRailComments(info: RailCommentsInfo | null): HTMLElement | null {
  const serialized = info ? JSON.stringify(info) : null;
  useLayoutEffect(() => {
    useRailCommentsStore.getState().setInfo(serialized ? (JSON.parse(serialized) as RailCommentsInfo) : null);
  }, [serialized]);
  useLayoutEffect(() => () => useRailCommentsStore.getState().setInfo(null), []);
  return useRailCommentsStore((s) => s.slot);
}

/** The rail tab lives in the URL (?rail=comments) so a link can open it. */
export const RAIL_PARAM = "rail";
export type RailTab = "comments" | "chat";

export function railTabFrom(params: URLSearchParams): RailTab {
  return params.get(RAIL_PARAM) === "comments" ? "comments" : "chat";
}

export function withRailTab(params: URLSearchParams, tab: RailTab): URLSearchParams {
  const next = new URLSearchParams(params);
  if (tab === "comments") next.set(RAIL_PARAM, "comments");
  else next.delete(RAIL_PARAM);
  return next;
}
